import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../api/axios';
import Chatbot from '../components/Chatbot';
import OTPModal from '../components/OTPModal';
import VideoPlayer from '../components/VideoPlayer';

const Franchise = () => {
  const [backgroundMedia, setBackgroundMedia] = React.useState(null);

  React.useEffect(() => {
    const fetchBackground = async () => {
      try {
        // First try without isActive filter (like Home page does)
        const response = await api.get('/site-media', {
          params: { page: 'franchise', _t: Date.now() },
        });
        const entries = response.data || [];
        
        // Filter for active entries on client side
        const activeEntries = entries.filter((m) => m.isActive !== false);
        
        console.log('Franchise page - All entries:', entries);
        console.log('Franchise page - Active entries:', activeEntries);
        
        // Try to find exact match first
        let background = activeEntries.find((m) => m.section === 'franchise_hero_background');
        
        // If not found, try any active entry for franchise page
        if (!background && activeEntries.length > 0) {
          background = activeEntries[0];
        }
        
        console.log('Franchise page - Selected background:', background);
        
        if (background && background.url) {
          setBackgroundMedia(background);
        } else {
          console.warn('Franchise page - No valid background found');
          setBackgroundMedia(null);
        }
      } catch (error) {
        console.error('Error fetching franchise background:', error);
        setBackgroundMedia(null);
      }
    };

    fetchBackground();
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    investmentRange: '',
    experience: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [pendingEnquiry, setPendingEnquiry] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Send OTP instead of submitting directly
      await api.post('/email/franchise/otp', {
        email: formData.email,
        enquiryData: formData
      });
      
      setPendingEnquiry(formData);
      setShowOTPModal(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP. Please try again.');
      console.error('Franchise enquiry error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (otp, resend = false) => {
    if (resend) {
      // Resend OTP
      try {
        await api.post('/email/franchise/otp', {
          email: pendingEnquiry.email,
          enquiryData: pendingEnquiry
        });
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to resend OTP');
      }
      return;
    }

    // Verify OTP and submit enquiry
    try {
      await api.post('/email/franchise/verify', {
        email: pendingEnquiry.email,
        otp
      });

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        investmentRange: '',
        experience: '',
        message: '',
      });
      setPendingEnquiry(null);
      setShowOTPModal(false);
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const benefits = [
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Proven Concept',
      description: 'Join a scalable café model with a unique positioning in the specialty coffee market.',
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      title: 'Brand Value',
      description: 'Be part of a brand that celebrates bold Robusta coffee and premium experiences.',
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: 'Multi-Faceted Business',
      description: 'Operate a café that combines coffee, art, workshops, and community engagement.',
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: 'Growth Potential',
      description: 'Tap into the growing specialty coffee market with a differentiated offering.',
    },
  ];

  return (
    <div className="pt-20 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Media */}
        {backgroundMedia && backgroundMedia.mediaType === 'video' ? (
          <VideoPlayer
            videoUrl={backgroundMedia.url}
            autoplay={true}
            muted={true}
            className="absolute inset-0 z-0"
          />
        ) : backgroundMedia && backgroundMedia.url ? (
          <div 
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${backgroundMedia.url}${backgroundMedia.url.includes('?') ? '&' : '?'}v=${backgroundMedia.updatedAt || Date.now()})`,
            }}
          ></div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-coffee-darker to-coffee-dark"></div>
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-coffee-darkest/90 via-coffee-darker/75 to-coffee-dark/80 z-10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-coffee-amber/5 via-transparent to-coffee-gold/5 z-10"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto text-center relative z-20"
        >
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-coffee-amber mb-6">
            Franchise Opportunity
          </h1>
          <p className="text-xl text-coffee-light">
            Bring the Rabuste Coffee experience to your community
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-4xl font-heading font-bold text-coffee-amber mb-6 text-center">
            Join the Rabuste Movement
          </h2>
          <div className="bg-coffee-brown/20 rounded-lg p-8 md:p-12">
            <p className="text-lg text-coffee-light mb-6 leading-relaxed">
              Rabuste Coffee offers a unique franchise opportunity for entrepreneurs who want to be part of the specialty coffee revolution. Our model combines bold Robusta coffee, art gallery experiences, community workshops, and technology-driven customer engagement.
            </p>
            <p className="text-lg text-coffee-light leading-relaxed">
              We're looking for partners who share our passion for quality, innovation, and community building. If you're ready to bring this bold concept to your market, we'd love to hear from you.
            </p>
          </div>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-4xl font-heading font-bold text-coffee-amber mb-12 text-center">
            Why Franchise with Rabuste?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-gradient-to-br from-coffee-brown/30 via-coffee-brown/20 to-coffee-dark/20 rounded-2xl p-6 border border-coffee-amber/20 hover:border-coffee-amber/40 transition-all duration-300 group"
              >
                <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-coffee-amber/20 to-coffee-gold/20 border border-coffee-amber/30 text-coffee-amber group-hover:from-coffee-amber/30 group-hover:to-coffee-gold/30 transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:scale-110">
                  {benefit.icon}
                </div>
                <h3 className="text-2xl font-heading font-semibold text-coffee-amber mb-3 group-hover:text-coffee-gold transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-coffee-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-4xl font-heading font-bold text-coffee-amber mb-8 text-center">
            Requirements & Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-coffee-brown/20 rounded-lg p-6">
              <h3 className="text-2xl font-heading font-semibold text-coffee-amber mb-4">
                What We Look For
              </h3>
              <ul className="space-y-3 text-coffee-light">
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Passion for coffee and hospitality
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Commitment to brand values and quality
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Business experience (preferred)
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Suitable location for café operation
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Financial capacity for initial investment
                </li>
              </ul>
            </div>
            <div className="bg-coffee-brown/20 rounded-lg p-6">
              <h3 className="text-2xl font-heading font-semibold text-coffee-amber mb-4">
                What We Provide
              </h3>
              <ul className="space-y-3 text-coffee-light">
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Comprehensive training program
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Brand guidelines and marketing support
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Operations manual and systems
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Ongoing support and guidance
                </li>
                <li className="flex items-start">
                  <span className="text-coffee-amber mr-2">•</span>
                  Access to supplier networks
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Enquiry Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-br from-coffee-dark/95 via-coffee-darker/95 to-coffee-darkest/95 rounded-2xl p-8 md:p-12 border border-coffee-brown/30 shadow-2xl"
        >
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-coffee-amber mb-4">
              Get in Touch
            </h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-coffee-amber/60 to-transparent mx-auto"></div>
          </div>

          {submitted ? (
            <div className="text-center py-12 max-w-xl mx-auto">
              <div className="text-6xl mb-6">✅</div>
              <h3 className="text-3xl font-heading font-bold text-coffee-amber mb-4">
                Thank You!
              </h3>
              <p className="text-coffee-light/90 text-lg mb-8 leading-relaxed">
                We've received your franchise enquiry. Our team will contact you soon.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-coffee-amber hover:bg-coffee-gold text-coffee-darker px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:brightness-110"
              >
                Submit Another Enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-coffee-amber/90 text-sm font-medium mb-3">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-coffee-brown/50 border border-coffee-brown/40 text-coffee-cream rounded-xl px-5 py-4 text-base focus:outline-none focus:border-coffee-amber focus:ring-2 focus:ring-coffee-amber/30 focus:bg-coffee-brown/60 transition-all duration-200 placeholder:text-coffee-light/40"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-coffee-amber/90 text-sm font-medium mb-3">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-coffee-brown/50 border border-coffee-brown/40 text-coffee-cream rounded-xl px-5 py-4 text-base focus:outline-none focus:border-coffee-amber focus:ring-2 focus:ring-coffee-amber/30 focus:bg-coffee-brown/60 transition-all duration-200 placeholder:text-coffee-light/40"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-coffee-amber/90 text-sm font-medium mb-3">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-coffee-brown/50 border border-coffee-brown/40 text-coffee-cream rounded-xl px-5 py-4 text-base focus:outline-none focus:border-coffee-amber focus:ring-2 focus:ring-coffee-amber/30 focus:bg-coffee-brown/60 transition-all duration-200 placeholder:text-coffee-light/40"
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <label className="block text-coffee-amber/90 text-sm font-medium mb-3">
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City, State/Country"
                    className="w-full bg-coffee-brown/50 border border-coffee-brown/40 text-coffee-cream rounded-xl px-5 py-4 text-base focus:outline-none focus:border-coffee-amber focus:ring-2 focus:ring-coffee-amber/30 focus:bg-coffee-brown/60 transition-all duration-200 placeholder:text-coffee-light/40"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-coffee-amber/90 text-sm font-medium mb-3">
                    Investment Range
                  </label>
                  <div className="relative">
                    <select
                      name="investmentRange"
                      value={formData.investmentRange}
                      onChange={handleChange}
                      className="w-full bg-coffee-brown/50 border border-coffee-brown/40 text-coffee-cream rounded-xl px-5 py-4 text-base focus:outline-none focus:border-coffee-amber focus:ring-2 focus:ring-coffee-amber/30 focus:bg-coffee-brown/60 transition-all duration-200 appearance-none cursor-pointer shadow-sm focus:shadow-md"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23FF8C00' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1.25rem center',
                        backgroundSize: '1rem',
                        paddingRight: '3rem'
                      }}
                    >
                      <option value="" style={{ backgroundColor: '#1A0F0A', color: '#D7CCC8' }}>Select expected investment</option>
                      <option value="Under ₹50L" style={{ backgroundColor: '#1A0F0A', color: '#EFEBE9' }}>Under ₹50L</option>
                      <option value="₹50L - ₹1Cr" style={{ backgroundColor: '#1A0F0A', color: '#EFEBE9' }}>₹50L - ₹1Cr</option>
                      <option value="₹1Cr - ₹2Cr" style={{ backgroundColor: '#1A0F0A', color: '#EFEBE9' }}>₹1Cr - ₹2Cr</option>
                      <option value="₹2Cr - ₹5Cr" style={{ backgroundColor: '#1A0F0A', color: '#EFEBE9' }}>₹2Cr - ₹5Cr</option>
                      <option value="Over ₹5Cr" style={{ backgroundColor: '#1A0F0A', color: '#EFEBE9' }}>Over ₹5Cr</option>
                    </select>
                  </div>
                  <p className="mt-2 text-xs text-coffee-light/60 italic">
                    This helps us recommend the right café model.
                  </p>
                </div>
                <div>
                  <label className="block text-coffee-amber/90 text-sm font-medium mb-3">
                    Business Experience
                  </label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Brief description"
                    className="w-full bg-coffee-brown/50 border border-coffee-brown/40 text-coffee-cream rounded-xl px-5 py-4 text-base focus:outline-none focus:border-coffee-amber focus:ring-2 focus:ring-coffee-amber/30 focus:bg-coffee-brown/60 transition-all duration-200 placeholder:text-coffee-light/40"
                  />
                </div>
              </div>
              <div>
                <label className="block text-coffee-amber/90 text-sm font-medium mb-3">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  placeholder="Tell us about your interest in franchising with Rabuste Coffee..."
                  className="w-full bg-coffee-brown/50 border border-coffee-brown/40 text-coffee-cream rounded-xl px-5 py-4 text-base focus:outline-none focus:border-coffee-amber focus:ring-2 focus:ring-coffee-amber/30 focus:bg-coffee-brown/60 transition-all duration-200 placeholder:text-coffee-light/40 resize-none leading-relaxed"
                />
              </div>
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-coffee-amber hover:bg-coffee-gold text-coffee-darker py-5 rounded-xl font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:brightness-110"
                >
                  {loading ? 'Sending OTP...' : 'Request Franchise Details'}
                </button>
                <p className="mt-4 text-center text-sm text-coffee-light/70 italic">
                  We personally review all franchise enquiries within 24–48 hours.
                </p>
              </div>
            </form>
          )}
        </motion.div>
      </section>

      {/* OTP Modal */}
      <OTPModal
        isOpen={showOTPModal}
        onClose={() => {
          setShowOTPModal(false);
          setPendingEnquiry(null);
        }}
        email={pendingEnquiry?.email || ''}
        onVerify={handleOTPVerify}
        type="franchise"
      />

      <Chatbot />
    </div>
  );
};

export default Franchise;

